/**
 * The board's post-submit decisions, as pure functions.
 *
 * `useChipPlacementBetSubmit` states the contract in so many words: it pushes
 * the bets and the CALLER strips the chips of any bet that then disappears
 * from the store. The shared panel does that with a bet-store subscription and
 * two phase-driven reconcile passes (`ChipPlacementPanel.tsx:2124-2362`); those
 * live inside a 3600-line component and are not extractable without editing it,
 * so the decisions are re-implemented here — pure, so they are testable without
 * a store, a socket or a render.
 *
 * The store shapes are structural on purpose: this module must not import
 * `store/bet`, so a test can hand it two plain objects.
 */

import type { BirdieChips } from './birdieChips'

/** The only field of a bet these decisions read. */
export interface ReconcilableBet {
  betDetails?: { selectedItems?: readonly string[] } | undefined
}

export interface BetStoreSlice {
  /** Pushed, not yet acknowledged. `betPlaced` writes here AND to `betsById`. */
  unacceptedBets: Readonly<Record<string, ReconcilableBet | undefined>>
  /** `betRejected` purges from BOTH maps; `betAccepted` leaves only this one. */
  betsById: Readonly<Record<string, ReconcilableBet | undefined>>
}

/** betId → optionId, for the bets that actually hit the wire. */
export type TrackedBets = Record<string, string>

const optionOf = (bet: ReconcilableBet | undefined): string | undefined =>
  bet?.betDetails?.selectedItems?.[0]

/**
 * Build the betId → optionId map from the ids `submitAllBets` reports as sent,
 * resolving each one against the store it just wrote to (the submit hook
 * returns ids only). Anything already tracked is kept: a retry inside one round
 * adds to the map rather than replacing it.
 */
export function trackSubmittedBets(input: {
  sentIds: readonly string[]
  store: BetStoreSlice
  existing?: Readonly<TrackedBets>
}): TrackedBets {
  const tracked: TrackedBets = { ...(input.existing ?? {}) }
  for (const id of input.sentIds) {
    const optionId = optionOf(input.store.betsById[id] ?? input.store.unacceptedBets[id])
    if (optionId !== undefined) tracked[id] = optionId
  }
  return tracked
}

export interface ResolvedBets {
  /** The still-pending subset — assign it straight back to the tracking ref. */
  tracked: TrackedBets
  /** Accepted: the bet landed in `betsById`. Nothing to do to the board. */
  acceptedOptionIds: string[]
  /** Refused: the bet left `unacceptedBets` WITHOUT landing in `betsById`. */
  rejectedOptionIds: string[]
}

/**
 * Resolve tracked bets against a store snapshot. A bet that is still in
 * `unacceptedBets` has no outcome yet and stays tracked; one that has left it
 * is decided by whether `betsById` kept it (`betAccepted`) or purged it
 * (`betRejected`).
 */
export function resolveTrackedBets(input: {
  tracked: Readonly<TrackedBets>
  store: BetStoreSlice
}): ResolvedBets {
  const tracked: TrackedBets = {}
  const acceptedOptionIds: string[] = []
  const rejectedOptionIds: string[] = []
  for (const betId of Object.keys(input.tracked)) {
    const optionId = input.tracked[betId]
    if (optionId === undefined) continue
    if (input.store.unacceptedBets[betId] !== undefined) {
      tracked[betId] = optionId
      continue
    }
    if (input.store.betsById[betId] !== undefined) acceptedOptionIds.push(optionId)
    else rejectedOptionIds.push(optionId)
  }
  return { tracked, acceptedOptionIds, rejectedOptionIds }
}

export interface StuckBets {
  betIds: string[]
  /** De-duplicated: one option can carry more than one stuck bet. */
  optionIds: string[]
}

/**
 * The settle-time safety net. A real bet leaves `unacceptedBets` at PLACEMENT,
 * so anything still sitting there when the round settles was never registered
 * server-side — no `bet:rejected` ever arrived for it either. The authoritative
 * purge + refund is the global `race_settled` handler's job
 * (`isUnregisteredAtSettlement`); all this decides is which chips must stop
 * being drawn as live bets. Nothing stays tracked past this point — the round
 * is over, so no further outcome can arrive for it.
 */
export function stuckAtSettlement(input: {
  tracked: Readonly<TrackedBets>
  unacceptedBets: Readonly<Record<string, ReconcilableBet | undefined>>
}): StuckBets {
  const betIds: string[] = []
  const optionIds: string[] = []
  for (const betId of Object.keys(input.tracked)) {
    const optionId = input.tracked[betId]
    if (optionId === undefined) continue
    if (input.unacceptedBets[betId] === undefined) continue
    betIds.push(betId)
    if (!optionIds.includes(optionId)) optionIds.push(optionId)
  }
  return { betIds, optionIds }
}

/**
 * What the board does when a dropped `race_ready` is recovered at
 * `race_starting` (`shouldRecoverMissedRoundReset`).
 *
 * The recovery unlocks the single-shot submission guard so the new round can
 * bet at all — which means the ~1.5 s timer it then arms will submit WHATEVER
 * is on the board. Nothing clears the board at settlement, so after a completed
 * submission last round those chips are still there: unlocking without clearing
 * them re-bets last round's stakes on this round, at this round's odds, with no
 * tap from the player. The shared panel guards exactly this
 * (`ChipPlacementPanel.tsx:2431-2436`).
 *
 * Chips are kept ONLY when nothing was submitted (a held or all-failed
 * submission): those are the player's own unfinished intent for this round.
 */
export function missedReadyRecovery(input: { hadSuccessfulSubmission: boolean }): {
  keepChips: boolean
} {
  return { keepChips: !input.hadSuccessfulSubmission }
}

/** The subset of a server bet the restore needs. */
export interface RestorableBet {
  raceId: string
  betAmount: number
  betDetails?: { selectedItems?: readonly string[] } | undefined
}

/**
 * Rebuild the board from the server's bets after a refresh.
 *
 * Filtered by round: `useGetCurrentBets` keeps its list in `useState` and
 * refills it from an effect, so on the commit where the round rolls over it
 * still holds the PREVIOUS round's (won/lost) bets. Restoring those would draw
 * last round's chips on the new round, report its stake as this round's, and —
 * because the restore also latches the submission flags — lock the player out
 * of a round they never bet in.
 *
 * Stakes on one option ACCUMULATE: two accepted bets on the same option (a
 * second session at a different stake) are one chip of the summed amount, not
 * the last one seen.
 */
export function restorableChips(input: {
  bets: readonly RestorableBet[]
  roundId: string | undefined
  marketIdByOption: ReadonlyMap<string, string>
}): BirdieChips {
  const restored: BirdieChips = {}
  if (input.roundId === undefined) return restored
  for (const bet of input.bets) {
    if (bet.raceId !== input.roundId) continue
    const optionId = bet.betDetails?.selectedItems?.[0]
    if (optionId === undefined || optionId === '') continue
    const marketId = input.marketIdByOption.get(optionId)
    if (marketId === undefined) continue
    const current = restored[optionId]
    restored[optionId] = {
      optionId,
      marketId,
      amount: (current?.amount ?? 0) + bet.betAmount,
      count: (current?.count ?? 0) + 1
    }
  }
  return restored
}
